import { useEffect, useRef, useState } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'

import { FeatureFlags } from '@/helpers/featureFlags'

import { isOverlayEligibleNotificationType } from './overlayContentGate'
import {
  canShowOverlayNow,
  getActiveNotificationId,
  recordOverlayShown,
} from './overlayFrequencyCap'
import { hasDismissedNotificationOverlay } from './sessionDismissal'
import type { NotificationOverlayNotification } from './types'

/**
 * Policy layer that decides which (if any) candidate notification should be
 * handed to the `NotificationOverlay` component. The component itself is a
 * pure renderer — it does its own flag check and per-id dismissal dedup, but
 * explicitly leaves frequency-capping and content-type gating to a caller.
 * This hook is that caller-side policy:
 *
 * - Gated entirely behind `enable-notification-center`: with the flag off,
 *   this never runs any of the logic below and always returns `null`, so
 *   there's no behavior change from today.
 * - Content-type gate: only candidates whose `notificationType` is in the
 *   overlay's allowlist are ever considered (see overlayContentGate.ts).
 * - Session dismissal: a candidate already dismissed this session (tracked
 *   by the component via sessionDismissal.ts) is skipped in favor of the
 *   next eligible candidate, since showing it again would be a no-op.
 * - Frequency cap: once the overlay has been shown, a *different* candidate
 *   is not allowed to replace it until the cap window elapses. The
 *   currently-active candidate is exempt from the cap check so it isn't
 *   yanked away mid-display by its own appearance - this holds even across
 *   a remount/reload within the same session, since the active id is
 *   persisted alongside the cap timestamp (see overlayFrequencyCap.ts),
 *   not just held in this instance's own ref.
 *
 * Callers pass candidates in priority order; the first eligible one wins.
 *
 * Intended to have a single consumer (e.g. one provider near the app root).
 * The frequency cap is shared module/session state, not per-instance - a
 * second concurrent consumer would contend with the first for the same cap
 * window and active id.
 *
 * Client-only: `canShowOverlayNow`/`getActiveNotificationId` read
 * `sessionStorage`, which doesn't exist on the server, so a server render
 * and the client's first render can select different candidates (or none
 * vs. one) whenever real candidates are already available at SSR time.
 * Nothing calls this hook yet, so this can't manifest today - whichever
 * follow-up wires it to a real render call site needs to either keep
 * candidates client-only (e.g. derived from a query that doesn't run during
 * SSR) or defer this hook's result to after mount.
 */
export const useNotificationOverlayPolicy = (
  candidates: readonly NotificationOverlayNotification[],
  /**
   * Fired at most once per newly-selected candidate - the same moment
   * `recordOverlayShown` runs, so it fires exactly when the frequency cap
   * considers this candidate "shown", not on every render where `selected`
   * happens to still be non-null.
   */
  onShown?: (notification: NotificationOverlayNotification) => void
): NotificationOverlayNotification | null => {
  const isEnabled = useFlag(FeatureFlags.ENABLE_NOTIFICATION_CENTER)
  // useState's lazy initializer guarantees getActiveNotificationId() (a
  // sessionStorage read) runs exactly once, on mount - unlike
  // `useRef(getActiveNotificationId())`, which looks like a one-time read
  // but actually calls the read on every render (useRef just ignores the
  // result after the first). Seeded from storage, not `null`, so an
  // undismissed notice that was active before a remount/reload is
  // recognized as still active on this instance's very first render, rather
  // than losing its cap exemption.
  const [initialActiveId] = useState(getActiveNotificationId)
  const activeIdRef = useRef<string | null>(initialActiveId)

  // Read via a ref rather than listed as an effect dependency, so a caller
  // passing an inline callback - a fresh identity every render - doesn't
  // make the effect below re-run (and risk re-firing onShown) on every
  // render where `selected` hasn't actually changed.
  const onShownRef = useRef(onShown)
  useEffect(() => {
    onShownRef.current = onShown
  }, [onShown])

  const eligibleCandidates = isEnabled
    ? candidates.filter(
        (candidate) =>
          isOverlayEligibleNotificationType(candidate.notificationType) &&
          !hasDismissedNotificationOverlay(candidate.id)
      )
    : []

  // The currently-active candidate stays exempt from the cap even when a
  // higher-priority candidate has since been prepended ahead of it in the
  // list - otherwise `firstEligible` below would switch to that new
  // candidate, the cap would block it, and `selected` would fall through to
  // `null`, yanking away a notice the user never dismissed.
  const activeCandidate =
    eligibleCandidates.find(
      // oxlint-disable-next-line react/refs
      (candidate) => candidate.id === activeIdRef.current
    ) ?? null
  const firstEligible = eligibleCandidates[0] ?? null

  const selected =
    firstEligible !== null && canShowOverlayNow()
      ? firstEligible
      : activeCandidate

  useEffect(() => {
    if (!selected || activeIdRef.current === selected.id) return
    activeIdRef.current = selected.id
    recordOverlayShown(selected.id)
    onShownRef.current?.(selected)
  }, [selected])

  return selected
}

export default useNotificationOverlayPolicy
